import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { chatThemeStyle } from "../../helpers/utils/stylecss-utils";
import { BsArrowUp, BsChevronDown } from "react-icons/bs";
import { defaultChatBotSettings } from "../../consts/settings";
import PoweredBy from "./powered-by";
import SessionList from "./session-list";

// Widths of the bar's column. The host iframe is sized to the column plus a
// gutter that leaves room for shadows, so the transparent frame stays small.
export const TEXT_BAR_WIDTHS = { collapsed: 320, expanded: 544, open: 560 };
export const TEXT_BAR_GUTTER = 20;
export const TEXT_BAR_OPEN_HEIGHT = 640;
const MAX_TIPS = 3;
const HOVER_LEAVE_DELAY = 180;

export default function TextBarWidget({ settings, open, onOpenChange, onSubmit, onLayout, busy, messageCount, sessionList, children }) {
  const root = useRef(null);
  const input = useRef(null);
  const messageList = useRef(null);
  const hoverLeaveTimer = useRef(null);
  const pointerInside = useRef(false);
  const reportedLayout = useRef(null);
  const [question, setQuestion] = useState("");
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const tips = (Array.isArray(settings.tips) ? settings.tips : [])
    .map(tip => String(tip || "").trim()).filter(Boolean).slice(0, MAX_TIPS);
  const expanded = open || focused || hovered || question.length > 0;
  const width = TEXT_BAR_WIDTHS[open ? "open" : expanded ? "expanded" : "collapsed"];
  const title = settings.title || defaultChatBotSettings.title;

  const cancelHoverLeave = () => {
    clearTimeout(hoverLeaveTimer.current);
    hoverLeaveTimer.current = null;
  };
  const clearHover = () => { cancelHoverLeave(); pointerInside.current = false; setHovered(false); };
  // Resizing a cross-origin iframe can synthesize pointerenter at an old
  // coordinate. Expand only on actual movement so that event cannot reopen it.
  const onPointerMove = event => {
    if (event.pointerType === "touch") return;
    if (!pointerInside.current && window.parent !== window) window.parent.postMessage({ type: 'botcircuits_pointer_enter' }, '*');
    pointerInside.current = true;
    cancelHoverLeave();
    setHovered(true);
  };
  const onPointerLeave = useCallback(() => {
    pointerInside.current = false;
    clearTimeout(hoverLeaveTimer.current);
    // Keep suggestions stable while crossing the bar/chip edges or while the
    // host frame is resizing beneath the pointer.
    hoverLeaveTimer.current = setTimeout(() => { hoverLeaveTimer.current = null; setHovered(false); }, HOVER_LEAVE_DELAY);
  }, []);
  useEffect(() => {
    const onHostPointerLeave = event => {
      if (event.source === window.parent && event.data?.type === 'botcircuits_pointer_leave') onPointerLeave();
    };
    // Leaving an iframe blurs its window without necessarily blurring the
    // active input. Restore focus state when that window becomes active again.
    const onWindowBlur = () => { setFocused(false); onPointerLeave(); };
    const onWindowFocus = () => setFocused(Boolean(root.current?.contains(document.activeElement)));
    window.addEventListener('message', onHostPointerLeave);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);
    return () => {
      clearTimeout(hoverLeaveTimer.current);
      window.removeEventListener('message', onHostPointerLeave);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [onPointerLeave]);

  // Report the size the host should give the iframe. The open panel fills
  // whatever height the host allows; the bar and chips are measured.
  useLayoutEffect(() => {
    const frameWidth = width + TEXT_BAR_GUTTER * 2;
    const reportSize = height => {
      const previous = reportedLayout.current;
      if (previous?.width === frameWidth && previous?.height === height) return;
      reportedLayout.current = { width: frameWidth, height };
      onLayout(reportedLayout.current);
    };
    if (open) {
      reportSize(TEXT_BAR_OPEN_HEIGHT);
      return undefined;
    }
    const node = root.current;
    if (!node) return undefined;
    const report = () => reportSize(Math.ceil(node.getBoundingClientRect().height));
    report();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, width, onLayout]);

  useEffect(() => {
    if (open && messageList.current) messageList.current.scrollTop = messageList.current.scrollHeight;
  }, [open, messageCount]);

  const submit = async text => {
    const current = text.trim();
    if (!current || busy || submitting) return;
    setStarted(true);
    onOpenChange(true);
    setSubmitting(true);
    setError("");
    try {
      await onSubmit(current);
      setQuestion("");
    } catch {
      setError("Your message couldn't be sent. Please try again.");
    } finally {
      setSubmitting(false);
      input.current?.focus();
    }
  };

  const onFocus = () => {
    if (started) onOpenChange(true);
  };
  // Stay expanded while focus moves between the input and the chips.
  const onBlur = event => {
    if (!root.current?.contains(event.relatedTarget)) setFocused(false);
  };
  const onKeyDown = event => {
    if (event.key !== "Escape") return;
    clearHover();
    setFocused(false);
    onOpenChange(false);
    if (root.current?.contains(document.activeElement)) document.activeElement.blur();
  };

  return (
    <div ref={root} onKeyDown={onKeyDown} onFocus={() => setFocused(true)} onBlur={onBlur} style={chatThemeStyle(settings)}
      className={`tb-root${open ? " is-open" : ""}${settings.position === "bottom-center" ? " is-centered" : ""}`}>
      <div className="tb-column" onPointerMove={onPointerMove} onPointerLeave={onPointerLeave}>
        {open ? (
          <section className="tb-panel" aria-label={`${title} chat`} style={{ backgroundColor: settings.chatWindowBackgroundColor }}>
            <header className="tb-panel-header">
              <SessionList {...sessionList}>
              <span className="tb-panel-logo"><img src={settings.logo || defaultChatBotSettings.logo} alt="" /></span>
              <span className="tb-panel-title">{title}</span>
              </SessionList>
              <button type="button" className="chat-icon-button" aria-label="Minimize chat" title="Minimize chat"
                onClick={() => { onOpenChange(false); setFocused(false); clearHover(); }}>
                <BsChevronDown size={16} />
              </button>
            </header>
            <div ref={messageList} className="chat-message-wrapper scrollbars">{children}</div>
            {/* Branding sits at the foot of the chat view, never under the bar. */}
            {settings.showPoweredBy !== false && <PoweredBy className="tb-panel-footer" />}
          </section>
        ) : expanded && tips.length > 0 && (
          <div className="tb-chips" style={{ maxWidth: TEXT_BAR_WIDTHS.collapsed }} role="group" aria-label="Suggested questions">
            {tips.map(tip => (
              <button key={tip} type="button" className="tb-chip" disabled={busy || submitting}
                onMouseDown={event => event.preventDefault()} onClick={() => submit(tip)}>
                {tip}
              </button>
            ))}
          </div>
        )}
        <form className={`tb-bar${settings.textBarGlowEnabled !== false ? " has-glow" : ""}`} onSubmit={event => { event.preventDefault(); submit(question); }}
          style={{ backgroundColor: settings.sendBarBackgroundColor,
            "--tb-glow-color": settings.textBarGlowColor || defaultChatBotSettings.textBarGlowColor }}>
          <input ref={input} type="text" maxLength={500} autoComplete="off" name="chat-text-input" aria-label="Message"
            className="tb-input" value={question} disabled={submitting}
            placeholder={settings.inputPlaceHolder || "Ask anything…"}
            onChange={event => setQuestion(event.target.value)} onFocus={onFocus} />
          <button type="submit" className="tb-send" aria-label="Send message" disabled={!question.trim() || busy || submitting}
            style={{ backgroundColor: "var(--chat-action)", color: "var(--chat-on-action)" }}>
            <BsArrowUp size={18} />
          </button>
        </form>
        {error && <p className="chat-error" role="alert">{error}</p>}
      </div>
    </div>
  );
}
